<script setup lang="ts">
/**
 * UiButton — основная кнопка кабинета (inbox-114).
 *
 * Variants: primary | outline | ghost | danger
 * Sizes:    sm | md (default) | lg
 * Props:    block | disabled | loading
 *
 * CSS ports'нуто из docs/mockups/dashboard-v1.html `.btn` + lib/components.css
 * (btn-ghost / btn-danger / btn-sm / btn-lg / btn-block).
 *
 * Usage:
 *   <UiButton @click="save">Сохранить</UiButton>
 *   <UiButton variant="outline" size="sm">Отмена</UiButton>
 *   <UiButton variant="danger" :loading="deleting" block>Удалить</UiButton>
 */

interface Props {
  variant?: 'primary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  /** inbox-461 — явный form-prop. Когда submit-кнопка модалки рендерится
   * вне <form> (в UiModal #footer slot), HTML attribute `form="ID"`
   * связывает кнопку с формой по ID. Без явного declaration атрибут
   * пробрасывался через fallthrough, но не всегда доходил до нативного
   * <button> (зависит от inheritAttrs и наличия non-prop attrs у parent'а).
   * Делаем явным — submit модалок (bio/campaign/...) стабильно работает. */
  form?: string;
}

withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  block: false,
  disabled: false,
  loading: false,
  type: 'button',
  form: undefined,
});
</script>

<template>
  <button
    :type="type"
    :form="form"
    :disabled="disabled || loading"
    class="ui-btn"
    :class="[
      `variant-${variant}`,
      `size-${size}`,
      { block, loading },
    ]"
  >
    <span
      v-if="loading"
      class="spinner"
      aria-hidden="true"
    />
    <span class="content">
      <slot />
    </span>
  </button>
</template>

<style scoped>
/* inbox-330 — унифицирован с UiInput/UiSelect: r=8 (--radius-input), md
   h=40 (как inputs в той же модалке), sm h=32 (chips), lg h=48. */
.ui-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  /* inbox-359 — кнопочный текст НЕ должен переноситься. Без nowrap
   * SVG-icon + text внутри `.content span` оборачивались на 2 строки
   * когда natural width content area был меньше суммы (наблюдено на
   * /bio button «+ Создать», h=54 вместо 40 из-за wrap'а). */
  white-space: nowrap;
  border-radius: var(--radius-input);
  border: 1px solid transparent;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s, opacity 0.12s;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  line-height: 1.2;
}

.ui-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* ── Sizes ── */
.size-sm {
  min-height: 32px;
  padding: 6px 12px;
  font-size: 13px;
}
.size-md {
  min-height: 40px;
  padding: 10px 16px;
  font-size: var(--fs-body);
}
.size-lg {
  min-height: 48px;
  padding: 12px 22px;
  font-size: 15px;
}

.block {
  display: flex;
  width: 100%;
}

/* ── Variants ── */
.variant-primary {
  background: var(--color-accent);
  color: #fff;
  border-color: var(--color-accent);
}
.variant-primary:hover:not(:disabled) {
  background: var(--color-accent-hi);
  border-color: var(--color-accent-hi);
}

.variant-outline {
  background: var(--color-bg-elev);
  color: var(--color-fg);
  border-color: var(--color-border);
}
.variant-outline:hover:not(:disabled) {
  background: var(--color-bg-card);
  border-color: var(--color-fg-muted);
}

.variant-ghost {
  background: transparent;
  color: var(--color-fg-muted);
  border-color: transparent;
}
.variant-ghost:hover:not(:disabled) {
  background: var(--color-bg-card);
  color: var(--color-fg);
}

.variant-danger {
  background: var(--color-danger);
  color: #fff;
  border-color: var(--color-danger);
}
.variant-danger:hover:not(:disabled) {
  filter: brightness(1.1);
}

/* ── Icon support ── */
.ui-btn :deep(svg) {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

/* ── Loading ── */
.ui-btn.loading .content {
  opacity: 0.6;
}
/* inbox-359 — Tailwind preflight даёт SVG `display: block` глобально.
 * В кнопке с svg-icon + text inside `.content` это растягивает контент
 * на 2 строки (svg + text) → h=54 вместо 40. Делаем content flex чтобы
 * children выровнялись в одну строку. */
.content {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.content svg {
  flex-shrink: 0;
}
.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
